import { useState, useRef, useEffect, useMemo } from 'react';
import { addListener } from '../../utils/event.js';
import { callEach } from '../../utils/fn.js';
import { mergeProps } from '../../utils/merge-props.js';
import { usePrevious } from '../use-previous.js';
import { registerWheelScrub } from './scrub-wheel.js';
const ALT_SCRUB_TARGET = 'data-alt-scrub-target';
const wheelCtxRef = { current: null };
let scrubEl = null;
let scrubSubscribers = 0;
let stableWheelCtx = null;
let unregisterAltScrub = () => { };
function useAltScrub(ctxIn) {
    const [wheelScrubbing, setWheelScrubbing] = useState(false);
    const ctx = mergeProps(ctxIn, {
        onScrubStart() {
            setWheelScrubbing(true);
        },
        onScrubEnd() {
            setWheelScrubbing(false);
        },
    });
    const ctxRef = useRef(ctx);
    ctxRef.current = ctx;
    const ctxInRef = useRef(ctxIn);
    ctxInRef.current = ctxIn;
    // If ctxIn ever changes, and it was registered as the wheelCtx, we need to update it
    // so they're kept in sync
    const lastCtx = usePrevious(ctxIn);
    useEffect(() => {
        if (stableWheelCtx === lastCtx) {
            wheelCtxRef.current = ctx;
            stableWheelCtx = ctxIn;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ctxIn]);
    useEffect(() => {
        if (scrubSubscribers === 0)
            registerAltScrub();
        ++scrubSubscribers;
        return () => {
            --scrubSubscribers;
            if (scrubSubscribers === 0)
                unregisterAltScrub();
        };
    }, []);
    const altScrubProps = useMemo(() => ({
        onPointerEnter(e) {
            scrubEl = e.currentTarget;
            wheelCtxRef.current = ctxRef.current;
            stableWheelCtx = ctxInRef.current;
            if (e.altKey) {
                enableAltScrub();
            }
        },
        onPointerLeave() {
            disableAltScrub();
            scrubEl = null;
            wheelCtxRef.current = null;
            stableWheelCtx = null;
        },
    }), []);
    return [wheelScrubbing, altScrubProps];
}
function registerAltScrub() {
    const unregisterAltScrubListeners = callEach(addListener(document, 'keydown', (e) => {
        if (e.key === 'Alt')
            enableAltScrub();
    }), addListener(document, 'keyup', (e) => {
        if (e.key === 'Alt')
            disableAltScrub();
    }), 
    // if the mouse is over the scrubEl when the window loses focus, we won't get a corresponding
    // pointerleave and we'd be stuck in an invalid state, so we clean up on blur
    addListener(window, 'blur', disableAltScrub));
    unregisterAltScrub = () => {
        unregisterAltScrubListeners();
        unregisterAltScrub = () => { };
    };
}
let unregisterWheelScrub = () => { };
function enableAltScrub() {
    if (scrubEl) {
        scrubEl.setAttribute(ALT_SCRUB_TARGET, '');
        if (wheelCtxRef.current) {
            unregisterWheelScrub = registerWheelScrub(scrubEl, wheelCtxRef);
        }
    }
}
function disableAltScrub() {
    if (scrubEl) {
        scrubEl.removeAttribute(ALT_SCRUB_TARGET);
        unregisterWheelScrub();
        unregisterWheelScrub = () => { };
    }
}

export { useAltScrub };
//# sourceMappingURL=use-scrub-alt.js.map
